import React from "react";
import { PHASE_3_PALETTE } from "./palette";

// Phase 3 (B1): horizontal grouped bar chart mirroring the example PDF.
//
// Layout: one row per item, two bars per row (current + prior). Bars share the
// same horizontal axis scale derived from the largest absolute value across both
// series so the visual comparison is faithful.
//
// Pure SVG — no chart library — so the DOM is stable for html-to-image during
// PDF export (no canvas/timing issues, no ResizeObserver layout shifts).
//
// Props
//   data           Array<{ label: string, current: number, prior?: number|null }>
//   width          number  optional, defaults to 720 (fits A4 portrait)
//   currentLabel   string  legend text for the current series (e.g. "2024")
//   priorLabel     string  legend text for the prior series (e.g. "2023")
//   formatValue    fn      formats values for labels (default: locale string)
//   barHeight      number  pixels per bar (default 8)
//   rowGap         number  pixels between rows (default 14)
//   labelWidth     number  pixels reserved for left-side labels (default 110)
const ComparisonBarChart = ({
  data,
  width = 720,
  currentLabel = "2024",
  priorLabel = "2023",
  formatValue = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }),
  barHeight = 8,
  rowGap = 10,
  labelWidth = 110,
}) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  // Phase 3 fix: when every row has a null prior we are in single-period mode.
  // The chart should collapse to one bar per row (no second slot wasted) and
  // hide the "Prior" legend item — otherwise users see an orphan legend entry
  // and the chart consumes twice the vertical space it needs.
  const singlePeriod = data.every((d) => d.prior == null);

  const padding = { top: 24, right: 96, bottom: 8, left: 8 };
  const innerWidth = width - padding.left - padding.right - labelWidth;
  const rowHeight = singlePeriod ? barHeight : barHeight * 2 + 4;
  const innerHeight = data.length * (rowHeight + rowGap);
  const height = padding.top + innerHeight + padding.bottom;

  const maxValue = Math.max(
    1,
    ...data.flatMap((d) => [Math.abs(d.current || 0), Math.abs(d.prior || 0)])
  );

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Legend */}
      <g transform={`translate(${padding.left + labelWidth}, 12)`} fontSize="11" fill={PHASE_3_PALETTE.text}>
        <rect x="0" y="-8" width="14" height="10" fill={PHASE_3_PALETTE.current} />
        <text x="20" y="0">{currentLabel}</text>
        {/* Hide the "Prior" legend item in single-period mode — otherwise it
            looks like there's a missing second series in the chart. */}
        {!singlePeriod && (
          <>
            <rect x="80" y="-8" width="14" height="10" fill={PHASE_3_PALETTE.prior} />
            <text x="100" y="0">{priorLabel}</text>
          </>
        )}
      </g>

      {/* Rows */}
      {data.map((row, i) => {
        const y = padding.top + i * (rowHeight + rowGap);
        const currentWidth = (Math.abs(row.current || 0) / maxValue) * innerWidth;
        const priorWidth = row.prior == null ? 0 : (Math.abs(row.prior) / maxValue) * innerWidth;
        return (
          <g key={row.label + "_" + i} transform={`translate(${padding.left}, ${y})`}>
            {/* Y label */}
            <text
              x={labelWidth - 6}
              y={rowHeight / 2}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="10"
              fill={PHASE_3_PALETTE.text}
            >
              {row.label}
            </text>
            {/* Current bar */}
            <rect
              x={labelWidth}
              y={0}
              width={currentWidth}
              height={barHeight}
              fill={PHASE_3_PALETTE.current}
            />
            {/* Prior bar (only when comparison data present) */}
            {row.prior != null && (
              <rect
                x={labelWidth}
                y={barHeight + 4}
                width={priorWidth}
                height={barHeight}
                fill={PHASE_3_PALETTE.prior}
              />
            )}
            {/* Value labels */}
            <text
              x={labelWidth + currentWidth + 4}
              y={barHeight / 2}
              dominantBaseline="middle"
              fontSize="9"
              fill={PHASE_3_PALETTE.text}
            >
              {formatValue(row.current)}
            </text>
            {row.prior != null && (
              <text
                x={labelWidth + priorWidth + 4}
                y={barHeight + 4 + barHeight / 2}
                dominantBaseline="middle"
                fontSize="9"
                fill={PHASE_3_PALETTE.text}
              >
                {formatValue(row.prior)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default ComparisonBarChart;
